import { QueryClient } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import {
  GetUserResponse,
  Organization,
  SnakedUser,
  User,
} from '../../network/httpClients/PlatformClient/api.schema';
import { QUERY_KEYS } from '../../network/queries/consts';
import { getDeploymentConfig } from '../../services/config';
import { transformKeysToCamelCase } from '../../utils/caseTransformers';
import { isLocalhost } from '../../utils/dynamicSettings';
import { isAirgappedMode } from '../../utils/env';
import { getOrGenerateValueFromLocalStorage } from '../../utils/localStorage';
import {
  getTraceIdFromSessionStorage,
  getSessionIdFromSessionStorage,
  getInitClientFromSessionStorage,
} from '../../utils/url';
import { getTeamId } from '../../utils/user';
import { getMixpanelApiHost, PROPERTIES } from './consts';
import { BaseProperties, EventProperties, UserData } from './types';
import { EventName } from './types';
import {
  buildBillingEventContext,
  buildCommonEventFields,
  getCachedOrganization,
  type MixpanelCommonEventFields,
} from './utils';

class Mixpanel {
  private static instance: Mixpanel | undefined;
  private isInitialized: boolean = false;
  private queryClient: QueryClient | undefined;

  static getInstance() {
    if (!this.instance) {
      this.instance = new Mixpanel();
    }

    return this.instance;
  }

  private createUserData(user: User, commonFields: MixpanelCommonEventFields) {
    const { organizationId, tierLevel } = user;
    const teamId = getTeamId(user);

    const userData: UserData = {
      organization_id: organizationId,
      tier_level: tierLevel,
      ...commonFields,
    };

    if (teamId) {
      userData.team_id = teamId;
    }

    return userData;
  }

  private createBaseProperties(
    user: SnakedUser | undefined,
    organization: Organization | undefined,
  ) {
    const transformedUser = user
      ? (transformKeysToCamelCase(user) as User)
      : undefined;

    const commonFields = buildCommonEventFields(transformedUser, organization);

    const baseProperties: BaseProperties = {
      product: 'portal',
      internal_event: true,
      platform_context: {
        page_url: window.location.href,
      },
    };

    if (transformedUser) {
      if (transformedUser.email) {
        baseProperties.$email = transformedUser.email;
      }

      baseProperties.user_data = this.createUserData(
        transformedUser,
        commonFields,
      );
    }

    return baseProperties;
  }

  init(queryClient: QueryClient) {
    if (isAirgappedMode()) {
      return;
    }

    if (this.isInitialized) {
      if (!isLocalhost()) {
        console.warn('Mixpanel already initialized');
      }

      return;
    }

    // Held before the token guard below so the event payload can still be
    // resolved (and logged) on deployments without a token, such as local dev.
    this.queryClient = queryClient;

    const mixpanelConfig = getDeploymentConfig().features?.mixpanel;

    if (!mixpanelConfig?.token) {
      if (!isLocalhost()) {
        console.warn(
          'Mixpanel is not initialized. No token in deployment config.',
        );
      }

      return;
    }

    console.log('📊 Initializing Mixpanel...');

    mixpanel.init(mixpanelConfig.token, {
      api_host: getMixpanelApiHost(),
      api_routes: { track: 't/', engage: 'e/', record: 'r/' },
      persistence: 'localStorage',
      ignore_dnt: true,
      record_sessions_percent: 0,
      record_mask_all_text: false,
      record_mask_all_inputs: false,
    });

    mixpanel.register_once({
      [PROPERTIES.BROWSER_UNIQUE_ID]: getOrGenerateValueFromLocalStorage(
        LOCAL_STORAGE_KEYS.BROWSER_UNIQUE_ID,
      ),
    });

    this.isInitialized = true;
  }

  track(eventName: EventName, properties: EventProperties) {
    if (isAirgappedMode()) {
      return;
    }

    if (!properties) {
      if (!isLocalhost()) {
        console.warn('No properties provided to Mixpanel track');
      }

      return;
    }

    const cachedUserResponse = this.queryClient?.getQueryData([
      QUERY_KEYS.USER,
    ]) as GetUserResponse | undefined;

    const cachedOrganization = getCachedOrganization(this.queryClient);

    const sessionId = getSessionIdFromSessionStorage();
    const traceId = getTraceIdFromSessionStorage();
    const initClient = getInitClientFromSessionStorage();
    const authProtocol = getDeploymentConfig().auth_client_type;

    const enrichedProperties = {
      ...properties,
      interaction_details: {
        ...properties.interaction_details,
        ...(sessionId && { [PROPERTIES.LOGIN_SESSION_ID]: sessionId }),
        ...(traceId && { [PROPERTIES.TRACE_ID]: traceId }),
        [PROPERTIES.INIT_CLIENT]: initClient,
        [PROPERTIES.AUTH_PROTOCOL]: authProtocol,
      },
    };

    const finalProperties = {
      ...this.createBaseProperties(
        cachedUserResponse?.user as SnakedUser | undefined,
        cachedOrganization,
      ),
      ...buildBillingEventContext(this.queryClient),
      ...enrichedProperties,
    };

    // Logged before the guard below, and after the payload is assembled, so a
    // deployment without a token (local dev) can still inspect what would ship.
    if (isLocalhost()) {
      console.log(`[Mixpanel] ${eventName}`, finalProperties);
    }

    if (!Mixpanel.instance || !this.isInitialized) {
      if (!isLocalhost()) {
        console.warn('Mixpanel not initialized');
      }

      return;
    }

    mixpanel.track(eventName, finalProperties);
  }

  identify(userId: string) {
    const shouldIdentify = !!userId && this.isInitialized;

    if (!shouldIdentify) {
      return;
    }

    mixpanel.identify(userId);
  }

  setUserProperties(user: { email?: string | null }) {
    if (!this.isInitialized) {
      return;
    }

    mixpanel.people.set({
      $email: user.email,
    });
  }
}

export const mixpanelService = Mixpanel.getInstance();
